import React, { memo, useCallback, useMemo } from 'react';
import { Pressable, StyleSheet } from 'react-native';
import Animated, { FadeIn, FadeOut, LinearTransition } from 'react-native-reanimated';

import IconCircledClose from '@/assets/icons/circled-close';
import { InnerBorder } from '@/components/InnerBorder';
import { LineSeparator } from '@/components/LineSeparator';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { useIsCashCurrency } from '@/hooks/use-social-currency';
import { designSystem } from '@/styles/styles';
import { PromotionType } from '@/types/api.generated';
import { isIos } from '@/utils/constants-platform-specific';

import { useIsBetSlipSubmitting } from '../../hooks/betslip-hooks';
import { useSbkBetSlipStore } from '../../hooks/use-sbk-betslip-store';
import { PayoutInput } from '../PayoutInput';
import { StakeInput } from '../StakeInput';
import { ProfitBoostDetails } from './ProfitBoostDetails';
import { useStakeInputErrorMessage } from './hooks/use-stake-input-error-message';

// Reanimated layout animations cause ghost views on Android (Old Arch) due to
// a known bug (github.com/software-mansion/react-native-reanimated/issues/7440).
// Fixed in Reanimated 3.19+. Remove this guard after upgrading.
const layoutTransition = isIos ? LinearTransition.duration(150) : undefined;
const enteringTransition = isIos ? FadeIn.duration(150) : undefined;
const exitingTransition = isIos ? FadeOut.duration(150) : undefined;

export const BetInputs = memo(({ betId }: { betId: string }) => {
    const errorMessage = useStakeInputErrorMessage(betId);
    const isFocused = useSbkBetSlipStore(state => state.editBetId === betId);
    const displayStake = useSbkBetSlipStore(state => state.bets[betId]?.displayStake ?? '');
    const clearBetStake = useSbkBetSlipStore(state => state.actions.clearBetStake);
    const isBetSlipSubmitting = useIsBetSlipSubmitting();
    const promo = useSbkBetSlipStore(state => state.selectedPromos[betId]);
    const isCashCurrency = useIsCashCurrency();

    const hasError = !!errorMessage;
    const hasValue = displayStake && displayStake !== '0';

    const borderColor = useMemo(() => {
        if (hasError) return ds.colorValue.background.danger.strong;
        if (isFocused) return ds.colorValue.background.brand.emphasis;
        return designSystem.colors.neutral500;
    }, [hasError, isFocused]);

    const handleClear = useCallback(() => {
        clearBetStake(betId);
    }, [clearBetStake, betId]);

    return (
        <Box mt="s16">
            <Box py="s10" px="s16">
                <InnerBorder borderRadius="r16" borderColor={borderColor} />
                <Row gap="s16" alignItems="center">
                    <Box flex={1}>
                        <StakeInput betId={betId} hasError={hasError} />
                    </Box>
                    <Animated.View layout={layoutTransition}>
                        <LineSeparator vertical style={styles.separator} />
                    </Animated.View>
                    <Animated.View style={styles.payoutContainer} layout={layoutTransition}>
                        <PayoutInput betId={betId} />
                    </Animated.View>
                    {hasValue ? (
                        <Animated.View entering={enteringTransition} exiting={exitingTransition}>
                            <Pressable onPress={handleClear} hitSlop={8} disabled={isBetSlipSubmitting}>
                                <IconCircledClose />
                            </Pressable>
                        </Animated.View>
                    ) : null}
                </Row>
                {isCashCurrency && promo?.type === PromotionType.ProfitBoost ? (
                    <ProfitBoostDetails betId={betId} />
                ) : null}
            </Box>
            {errorMessage ? (
                <Text color={ds.colorToken.text.danger} variant="bodySmall" testID="wagerErrorMessage" mt="s8">
                    {errorMessage}
                </Text>
            ) : null}
        </Box>
    );
});

const styles = StyleSheet.create({
    separator: {
        height: 36,
    },
    payoutContainer: {
        flex: 1,
    },
});
